import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Utensils,
  Phone,
  MapPin,
  Clock,
  Star,
  Instagram,
  Facebook,
  ChefHat,
  Wine,
  Leaf,
  Award,
  ArrowLeft,
  MessageCircle,
  Sparkles,
  Quote,
  Plus,
  Minus,
} from "lucide-react";
import heroImg from "@/assets/demo/hero-restaurant.jpg";
import chefImg from "@/assets/demo/chef.jpg";
import wineCellarImg from "@/assets/demo/wine-cellar.jpg";
import diningRoomImg from "@/assets/demo/dining-room.jpg";
import dishRisotto from "@/assets/demo/dish-risotto.jpg";
import dishBurrata from "@/assets/demo/dish-burrata.jpg";
import dishTiramisu from "@/assets/demo/dish-tiramisu.jpg";
import dishSteak from "@/assets/demo/dish-steak.jpg";

export const Route = createFileRoute("/demo/osteria-del-borgo")({
  head: () => ({
    meta: [
      { title: "Osteria del Borgo — Ristorante italiano a Lugano (Demo FlashWeb)" },
      {
        name: "description",
        content:
          "Demo premium di FlashWeb: sito ristorante di livello internazionale con menù degustazione, prenotazioni WhatsApp, gallery editoriale e SEO locale.",
      },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: DemoRestaurant,
});

const PHONE = "+41765008176";
const WA_NUMBER = "41765008176";

const MENU = [
  {
    section: "Antipasti",
    items: [
      { name: "Burrata pugliese", desc: "Pomodori datterini confit, basilico fresco, olio EVO della Sabina", price: "18", img: dishBurrata },
      { name: "Tartare di manzo ticinese", desc: "Battuta al coltello, capperi di Pantelleria, tuorlo marinato", price: "24" },
      { name: "Vitello tonnato", desc: "Geraci affumicato, salsa tonnata della tradizione piemontese", price: "19" },
    ],
  },
  {
    section: "Primi",
    items: [
      { name: "Tagliolini al tartufo nero", desc: "Pasta tirata a mano, tartufo del Mendrisiotto, burro di malga", price: "28", img: chefImg },
      { name: "Risotto Carnaroli zafferano e ossobuco", desc: "Carnaroli Acquerello 7 anni, midollo, gremolada", price: "26", img: dishRisotto },
      { name: "Pici cacio, pepe e limone", desc: "Pici toscani, pecorino di fossa, pepe Sarawak", price: "22" },
    ],
  },
  {
    section: "Secondi",
    items: [
      { name: "Costata di Black Angus", desc: "Frollatura 45 giorni, rosmarino, patate al forno con sale Maldon", price: "42", img: dishSteak },
      { name: "Branzino in crosta di sale", desc: "Pesca del giorno, erbe aromatiche, olio al limone (per 2)", price: "68" },
      { name: "Cotoletta alla milanese", desc: "Vitello da latte, panatura doppia, insalata di campo", price: "34" },
    ],
  },
  {
    section: "Dolci",
    items: [
      { name: "Tiramisù della casa", desc: "Mascarpone fresco, savoiardi artigianali, caffè Marzocco", price: "12", img: dishTiramisu },
      { name: "Panna cotta ai frutti di bosco", desc: "Vaniglia Bourbon, coulis di mirtilli e lamponi", price: "11" },
    ],
  },
];

const REVIEWS = [
  { name: "Marco R.", role: "Imprenditore, Lugano", text: "Il miglior risotto allo zafferano della Svizzera italiana. Atmosfera che ti riporta in Toscana, servizio che ti fa sentire a casa.", rating: 5 },
  { name: "Sofia B.", role: "Food blogger", text: "Prenotato via WhatsApp in 10 secondi, accolti come amici di famiglia. Ogni piatto racconta una storia. Indimenticabile.", rating: 5 },
  { name: "Andrea M.", role: "Sommelier AIS", text: "Carta dei vini di rara intelligenza: 200 etichette con scelte coraggiose e ricarichi onesti. Lo chef merita la stella.", rating: 5 },
];

const PRESS = ["Gambero Rosso", "Michelin Guide", "Corriere del Ticino", "Identità Golose", "La Stampa"];

const FAQ = [
  { q: "Devo prenotare in anticipo?", a: "Sì, consigliamo di prenotare con almeno 2-3 giorni di anticipo nei weekend. Risposta entro 1 ora via WhatsApp." },
  { q: "Avete opzioni vegetariane o senza glutine?", a: "Assolutamente. Ogni sezione del menù ha alternative vegetariane e abbiamo pasta fresca senza glutine fatta in casa." },
  { q: "Organizzate eventi privati?", a: "Sì, la nostra sala privata accoglie fino a 28 persone per cene aziendali, compleanni e matrimoni intimi." },
  { q: "C'è parcheggio?", a: "Convenzione con l'autosilo di Piazza della Riforma a 2 minuti a piedi (CHF 5 forfait serale)." },
];

function DemoBanner() {
  // Discreet floating pill — non-intrusive, lets the site feel standalone
  return (
    <Link
      to="/"
      className="fixed bottom-5 left-5 z-50 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-black/70 px-3 py-1.5 text-[11px] font-medium text-amber-200/90 shadow-lg backdrop-blur transition hover:bg-black/90 hover:text-amber-200"
      aria-label="Torna a FlashWeb"
    >
      <ArrowLeft className="h-3 w-3" />
      <span>by FlashWeb</span>
    </Link>
  );
}

function ReservationForm() {
  const [name, setName] = useState("");
  const [people, setPeople] = useState("2");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("19:30");
  const [note, setNote] = useState("");

  const waHref = useMemo(() => {
    const msg = `Buongiorno Osteria del Borgo, vorrei prenotare un tavolo.%0A%0A👤 Nome: ${encodeURIComponent(name || "—")}%0A👥 Persone: ${people}%0A📅 Data: ${date || "—"}%0A🕒 Ora: ${time}%0A📝 Note: ${encodeURIComponent(note || "—")}`;
    return `https://wa.me/${WA_NUMBER}?text=${msg}`;
  }, [name, people, date, time, note]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        window.open(waHref, "_blank");
      }}
      className="grid gap-4 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-stone-900/90 to-black/90 p-7 text-stone-100 shadow-[0_30px_80px_-20px_rgba(245,158,11,0.25)] backdrop-blur sm:grid-cols-2"
    >
      <div className="sm:col-span-2">
        <span className="inline-block rounded-full bg-amber-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-300">
          Prenotazione istantanea
        </span>
        <h3 className="mt-3 font-serif text-3xl text-amber-300">Riserva il tuo tavolo</h3>
        <p className="mt-1 text-sm text-stone-300">Conferma in meno di 1 ora via WhatsApp. Nessuna app, nessuna registrazione.</p>
      </div>
      <label className="text-sm">
        Nome e cognome
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-md border border-stone-700 bg-stone-950/70 px-3 py-2.5 text-stone-100 transition focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
          placeholder="Mario Rossi"
        />
      </label>
      <label className="text-sm">
        Numero persone
        <select
          value={people}
          onChange={(e) => setPeople(e.target.value)}
          className="mt-1 w-full rounded-md border border-stone-700 bg-stone-950/70 px-3 py-2.5 text-stone-100 transition focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 10, 12].map((n) => (
            <option key={n} value={n}>{n} {n === 1 ? "persona" : "persone"}</option>
          ))}
        </select>
      </label>
      <label className="text-sm">
        Data
        <input
          required
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="mt-1 w-full rounded-md border border-stone-700 bg-stone-950/70 px-3 py-2.5 text-stone-100 transition focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
        />
      </label>
      <label className="text-sm">
        Ora
        <input
          required
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className="mt-1 w-full rounded-md border border-stone-700 bg-stone-950/70 px-3 py-2.5 text-stone-100 transition focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
        />
      </label>
      <label className="text-sm sm:col-span-2">
        Note (allergie, occasioni speciali, preferenze tavolo...)
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-md border border-stone-700 bg-stone-950/70 px-3 py-2.5 text-stone-100 transition focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
          placeholder="Anniversario, tavolo vicino alla finestra, intolleranza al glutine..."
        />
      </label>
      <button
        type="submit"
        className="group sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-green-500 to-green-400 px-6 py-3.5 font-semibold text-white shadow-lg shadow-green-500/30 transition hover:shadow-xl hover:shadow-green-500/40"
      >
        <MessageCircle className="h-5 w-5 transition group-hover:scale-110" />
        Prenota via WhatsApp
      </button>
      <p className="sm:col-span-2 text-center text-xs text-stone-400">
        ✓ Risposta entro 1 ora · ✓ Nessun anticipo richiesto · ✓ Cancellazione gratuita fino a 24h prima
      </p>
    </form>
  );
}

function FaqItem({ q, a, idx }: { q: string; a: string; idx: number }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-amber-500/15">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left transition hover:text-amber-300"
      >
        <span className="font-serif text-lg text-stone-100">{q}</span>
        {open ? <Minus className="h-5 w-5 flex-shrink-0 text-amber-400" /> : <Plus className="h-5 w-5 flex-shrink-0 text-amber-400" />}
      </button>
      {open && <p className="pb-5 pr-9 text-stone-400">{a}</p>}
    </div>
  );
}

function DemoRestaurant() {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "40%"]);
  const heroScale = useTransform(heroProgress, [0, 1], [1, 1.15]);
  const heroOpacity = useTransform(heroProgress, [0, 0.8], [1, 0]);

  return (
    <div className="min-h-screen bg-stone-950 font-serif text-stone-100 antialiased overflow-x-hidden">
      <DemoBanner />

      {/* Top nav */}
      <header className="absolute left-0 right-0 top-0 z-40">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-500 shadow-lg shadow-amber-500/30">
              <Utensils className="h-5 w-5 text-stone-900" />
            </div>
            <div>
              <div className="text-lg font-bold leading-tight tracking-wide text-white drop-shadow">
                Osteria del Borgo
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/80">Dal 1962 · Lugano</div>
            </div>
          </div>
          <nav className="hidden gap-7 text-sm font-medium text-white/90 md:flex">
            <a href="#menu" className="transition hover:text-amber-300">Menù</a>
            <a href="#about" className="transition hover:text-amber-300">Chi siamo</a>
            <a href="#gallery" className="transition hover:text-amber-300">Galleria</a>
            <a href="#contatti" className="transition hover:text-amber-300">Contatti</a>
          </nav>
          <a
            href="#prenota"
            className="rounded-md bg-amber-500 px-5 py-2.5 text-sm font-semibold text-stone-900 shadow-lg shadow-amber-500/20 transition hover:bg-amber-400 hover:shadow-xl"
          >
            Prenota un tavolo
          </a>
        </div>
      </header>

      {/* Hero with parallax */}
      <section ref={heroRef} className="relative h-screen min-h-[700px] w-full overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img
            src={heroImg}
            alt="Sala dell'Osteria del Borgo a Lugano illuminata da candele"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/95" />
        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-end px-6 pb-24">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border border-amber-400/40 bg-black/40 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.25em] text-amber-300 backdrop-blur"
          >
            <Award className="h-3.5 w-3.5" /> Gambero Rosso 2025 · 2 Forchette
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-4xl font-serif text-5xl font-bold leading-[1.05] text-white drop-shadow-2xl sm:text-7xl md:text-8xl"
          >
            Tre generazioni. <br />
            <em className="italic text-amber-300">Una sola passione.</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="mt-6 max-w-xl text-lg text-stone-200 sm:text-xl"
          >
            Cucina italiana autentica nel cuore di Lugano. Pasta fresca tirata ogni mattina, materie prime ticinesi,
            cantina con oltre 200 etichette.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            <a
              href="#prenota"
              className="group inline-flex items-center gap-2 rounded-md bg-amber-500 px-7 py-3.5 font-semibold text-stone-900 shadow-lg shadow-amber-500/30 transition hover:bg-amber-400 hover:shadow-xl"
            >
              <Utensils className="h-4 w-4 transition group-hover:rotate-12" /> Prenota un tavolo
            </a>
            <a
              href="#menu"
              className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/5 px-7 py-3.5 font-semibold text-white backdrop-blur transition hover:bg-white/10"
            >
              Esplora il menù →
            </a>
          </motion.div>
        </motion.div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
          <div className="h-12 w-7 rounded-full border-2 border-white/40 p-1.5">
            <motion.div
              animate={{ y: [0, 14, 0] }}
              transition={{ duration: 1.8, repeat: Infinity }}
              className="h-2 w-full rounded-full bg-amber-400"
            />
          </div>
        </div>
      </section>

      {/* Quick info strip */}
      <section className="border-y border-amber-500/10 bg-black/60 py-6">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-4 px-6 text-center sm:grid-cols-4">
          {[
            { Icon: Star, label: "4.9 / 5", sub: "312 recensioni Google" },
            { Icon: Clock, label: "Aperto oggi", sub: "12:00 – 23:00" },
            { Icon: MapPin, label: "Via Nassa 12", sub: "Lugano, centro" },
            { Icon: Award, label: "Gambero Rosso", sub: "2025 · 2 Forchette" },
          ].map(({ Icon, label, sub }) => (
            <div key={label} className="flex flex-col items-center">
              <Icon className="mb-2 h-5 w-5 text-amber-400" />
              <div className="text-sm font-semibold text-white">{label}</div>
              <div className="text-xs text-stone-400">{sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Press strip */}
      <section className="border-b border-amber-500/10 bg-stone-950 py-8">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-4 text-center text-[11px] uppercase tracking-[0.3em] text-stone-500">
            Hanno scritto di noi
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-center">
            {PRESS.map((p) => (
              <span key={p} className="font-serif text-base italic text-stone-400 sm:text-lg">{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* About with sticky image */}
      <section id="about" className="relative mx-auto max-w-7xl px-6 py-32">
        <div className="grid items-start gap-16 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="md:sticky md:top-24"
          >
            <span className="text-xs uppercase tracking-[0.35em] text-amber-400">La nostra storia</span>
            <h2 className="mt-4 font-serif text-4xl font-bold leading-tight sm:text-6xl break-words">
              Dal 1962, <br />
              <em className="italic text-amber-300">l'arte di accogliere.</em>
            </h2>
            <div className="mt-8 space-y-5 text-lg leading-relaxed text-stone-300">
              <p>
                Nonna Lucia aprì la prima osteria in un vicolo del borgo vecchio di Lugano con un solo principio:
                ogni piatto doveva sapere <strong className="text-amber-300">di casa, di mani, di tempo</strong>.
              </p>
              <p>
                Oggi, tre generazioni dopo, la famiglia Belloni continua quella tradizione con prodotti ticinesi
                di stagione, pasta tirata a mano ogni mattina, pane lievitato 36 ore e salumi affinati nella nostra
                cantina di pietra.
              </p>
              <p className="font-serif italic text-amber-200/90">
                "Non serviamo cibo. Raccontiamo una storia, una portata alla volta."
                <span className="mt-2 block text-sm not-italic text-stone-500">— Chef Marco Belloni</span>
              </p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-amber-500/15 pt-8">
              {[
                { Icon: ChefHat, label: "Chef stellato" },
                { Icon: Leaf, label: "Km 0 ticinese" },
                { Icon: Wine, label: "200+ etichette" },
              ].map(({ Icon, label }) => (
                <div key={label} className="text-center">
                  <Icon className="mx-auto h-7 w-7 text-amber-400" />
                  <div className="mt-2 text-[11px] uppercase tracking-wider text-stone-400">{label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <div className="space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative aspect-[4/5] overflow-hidden rounded-2xl border border-amber-500/20 shadow-2xl"
            >
              <img src={chefImg} alt="Chef Marco Belloni che impiatta tagliolini al tartufo" className="h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/60 to-transparent p-6">
                <div className="font-serif text-2xl text-amber-300">Chef Marco Belloni</div>
                <div className="text-sm text-stone-300">15 anni di stelle Michelin tra Milano e New York</div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-amber-500/20 shadow-2xl"
            >
              <img src={diningRoomImg} alt="Sala con vista sul lago di Lugano" className="h-full w-full object-cover" loading="lazy" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Parallax wine cellar quote */}
      <section className="relative h-[80vh] min-h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={wineCellarImg} alt="Cantina dei vini con oltre 200 etichette" className="h-full w-full object-cover" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-black/80" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl items-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-150px" }}
            transition={{ duration: 1 }}
            className="max-w-2xl"
          >
            <Quote className="mb-6 h-10 w-10 text-amber-400" />
            <p className="font-serif text-3xl font-light italic leading-snug text-white sm:text-5xl">
              "Una cantina con la stessa attenzione di una <span className="text-amber-300">biblioteca antica</span> — qui si beve la memoria del territorio."
            </p>
            <div className="mt-8 text-sm uppercase tracking-[0.3em] text-amber-300">
              — Identità Golose, 2024
            </div>
          </motion.div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="bg-gradient-to-b from-stone-950 via-stone-900/40 to-stone-950 py-32">
        <div className="mx-auto max-w-6xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-16 text-center"
          >
            <span className="text-xs uppercase tracking-[0.35em] text-amber-400">La carta</span>
            <h2 className="mt-4 font-serif text-5xl font-bold sm:text-6xl break-words">
              Il nostro <em className="italic text-amber-300">menù</em>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-stone-400">
              Aggiornato settimanalmente secondo il mercato e la stagione. Pasta fresca quotidiana, materie prime ticinesi.
            </p>
          </motion.div>

          <div className="space-y-20">
            {MENU.map((section, sIdx) => (
              <motion.div
                key={section.section}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6 }}
              >
                <div className="mb-8 flex items-end justify-between border-b border-amber-500/20 pb-3">
                  <h3 className="font-serif text-3xl text-amber-300 sm:text-4xl break-words">{section.section}</h3>
                  <span className="text-xs uppercase tracking-wider text-stone-500">0{sIdx + 1} / 0{MENU.length}</span>
                </div>
                <div className="grid gap-x-12 gap-y-8 md:grid-cols-2">
                  {section.items.map((item) => (
                    <div key={item.name} className="group flex gap-5">
                      {item.img && (
                        <div className="h-24 w-24 flex-shrink-0 overflow-hidden rounded-lg border border-amber-500/20 sm:h-28 sm:w-28">
                          <img
                            src={item.img}
                            alt={item.name}
                            loading="lazy"
                            className="h-full w-full object-cover transition duration-700 group-hover:scale-110"
                          />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="flex items-baseline justify-between gap-3">
                          <h4 className="font-serif text-xl text-stone-100">{item.name}</h4>
                          <span className="flex-shrink-0 font-serif text-lg font-semibold text-amber-400">CHF {item.price}</span>
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-stone-400">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-20 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-transparent p-10 text-center"
          >
            <Sparkles className="mx-auto h-7 w-7 text-amber-400" />
            <h3 className="mt-4 font-serif text-3xl text-amber-300">Menù degustazione "Tre Generazioni"</h3>
            <p className="mx-auto mt-3 max-w-xl text-stone-300">
              7 portate firmate dallo chef, abbinamento vini opzionale. La nostra esperienza più completa.
            </p>
            <div className="mt-5 font-serif text-4xl text-white">CHF 95 <span className="text-base text-stone-400">/ persona</span></div>
            <div className="text-sm text-stone-500">+ CHF 45 abbinamento vini selezionati dal sommelier</div>
          </motion.div>
        </div>
      </section>

      {/* Gallery editorial */}
      <section id="gallery" className="mx-auto max-w-7xl px-6 py-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 text-center"
        >
          <span className="text-xs uppercase tracking-[0.35em] text-amber-400">Atmosfera</span>
          <h2 className="mt-4 font-serif text-5xl font-bold sm:text-6xl break-words">
            Lasciati <em className="italic text-amber-300">trasportare</em>
          </h2>
        </motion.div>

        <div className="grid grid-cols-12 gap-3 md:gap-4">
          {[
            { src: heroImg, span: "col-span-12 md:col-span-8 aspect-[16/10]", alt: "Sala principale" },
            { src: dishBurrata, span: "col-span-6 md:col-span-4 aspect-square", alt: "Burrata pugliese" },
            { src: dishSteak, span: "col-span-6 md:col-span-4 aspect-square", alt: "Costata Black Angus" },
            { src: diningRoomImg, span: "col-span-12 md:col-span-8 aspect-[16/10]", alt: "Sala con vista lago" },
            { src: wineCellarImg, span: "col-span-12 md:col-span-6 aspect-[5/3]", alt: "Cantina vini" },
            { src: dishRisotto, span: "col-span-6 md:col-span-3 aspect-square", alt: "Risotto zafferano" },
            { src: dishTiramisu, span: "col-span-6 md:col-span-3 aspect-square", alt: "Tiramisù" },
          ].map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: (i % 4) * 0.1 }}
              className={`group relative overflow-hidden rounded-xl border border-amber-500/10 ${img.span}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="h-full w-full object-cover transition duration-1000 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
            </motion.div>
          ))}
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-gradient-to-b from-stone-950 to-stone-900/60 py-32">
        <div className="mx-auto max-w-6xl px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-14 text-center"
          >
            <div className="flex items-center justify-center gap-1 text-amber-400">
              {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="h-6 w-6 fill-current" />)}
            </div>
            <div className="mt-3 text-sm text-stone-400">4.9 / 5 · 312 recensioni Google</div>
            <h2 className="mt-4 font-serif text-5xl font-bold sm:text-6xl break-words">
              Cosa dicono <em className="italic text-amber-300">i nostri ospiti</em>
            </h2>
          </motion.div>
          <div className="grid gap-6 md:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <motion.div
                key={r.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.15 }}
                className="group relative rounded-2xl border border-amber-500/20 bg-gradient-to-br from-stone-900/80 to-black/80 p-8 backdrop-blur transition hover:border-amber-500/40"
              >
                <Quote className="absolute right-6 top-6 h-8 w-8 text-amber-500/20 transition group-hover:text-amber-500/40" />
                <div className="flex gap-1 text-amber-400">
                  {Array.from({ length: r.rating }).map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="mt-5 font-serif text-lg italic leading-relaxed text-stone-200">"{r.text}"</p>
                <div className="mt-6 border-t border-amber-500/15 pt-4">
                  <div className="font-semibold text-amber-300">{r.name}</div>
                  <div className="text-xs text-stone-500">{r.role}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reservation + Contact */}
      <section id="prenota" className="mx-auto max-w-7xl px-6 py-32">
        <div className="grid gap-12 lg:grid-cols-5">
          <div id="contatti" className="lg:col-span-2">
            <span className="text-xs uppercase tracking-[0.35em] text-amber-400">Vieni a trovarci</span>
            <h2 className="mt-4 font-serif text-5xl font-bold leading-tight sm:text-6xl break-words">
              Dove e <em className="italic text-amber-300">quando</em>
            </h2>
            <div className="mt-10 space-y-6 text-stone-200">
              <div className="flex gap-4">
                <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-amber-400" />
                <div>
                  <div className="font-semibold">Via Nassa 12, 6900 Lugano</div>
                  <div className="text-sm text-stone-400">A 2 minuti a piedi da Piazza della Riforma</div>
                </div>
              </div>
              <div className="flex gap-4">
                <Phone className="mt-1 h-5 w-5 flex-shrink-0 text-amber-400" />
                <a href={`tel:${PHONE}`} className="font-semibold transition hover:text-amber-300">
                  {PHONE}
                </a>
              </div>
              <div className="flex gap-4">
                <Clock className="mt-1 h-5 w-5 flex-shrink-0 text-amber-400" />
                <div>
                  <div className="font-semibold">Orari di apertura</div>
                  <div className="mt-1 text-sm text-stone-400">
                    Mar – Dom · 12:00 – 14:30<br />
                    Mar – Dom · 19:00 – 23:00<br />
                    <span className="text-amber-300/70">Lunedì chiuso</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <a href="#" aria-label="Instagram" className="rounded-full border border-amber-500/30 p-3 transition hover:bg-amber-500/10">
                  <Instagram className="h-5 w-5 text-amber-300" />
                </a>
                <a href="#" aria-label="Facebook" className="rounded-full border border-amber-500/30 p-3 transition hover:bg-amber-500/10">
                  <Facebook className="h-5 w-5 text-amber-300" />
                </a>
              </div>
            </div>
            <div className="mt-10 overflow-hidden rounded-2xl border border-amber-500/20 shadow-2xl">
              <iframe
                title="Mappa Osteria del Borgo"
                src="https://www.openstreetmap.org/export/embed.html?bbox=8.945%2C45.998%2C8.960%2C46.008&layer=mapnik&marker=46.003%2C8.9525"
                className="h-72 w-full"
                loading="lazy"
              />
            </div>
          </div>

          <div className="lg:col-span-3">
            <ReservationForm />

            {/* FAQ */}
            <div className="mt-12">
              <h3 className="mb-4 font-serif text-2xl text-amber-300">Domande frequenti</h3>
              <div>
                {FAQ.map((f, i) => <FaqItem key={f.q} {...f} idx={i} />)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-800 bg-black py-10 text-center text-xs text-stone-500">
        <div className="mx-auto max-w-5xl px-6">
          <div className="font-serif text-2xl text-amber-300">Osteria del Borgo</div>
          <div className="mt-2">Via Nassa 12, 6900 Lugano · {PHONE}</div>
          <div className="mt-1">Mar – Dom · 12:00 – 14:30 · 19:00 – 23:00 · Lunedì chiuso</div>
          <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-stone-600">
            © {new Date().getFullYear()} Osteria del Borgo · P.IVA CHE-123.456.789
          </div>
        </div>
      </footer>
    </div>
  );
}
